import React, { useState, useEffect } from 'react';
import { Bug, Settings } from 'lucide-react';
import { useDebugLogger } from '../../hooks/useDebugLogger';
import { DebugPanel } from './DebugPanel';

interface GlobalDebugPanelProps {
  position?: 'bottom-right' | 'bottom-left' | 'top-right' | 'top-left';
}

export const GlobalDebugPanel: React.FC<GlobalDebugPanelProps> = ({ 
  position = 'bottom-right' 
}) => {
  const [isPanelOpen, setIsPanelOpen] = useState(false);
  const { config, stats, updateConfig } = useDebugLogger();
  
  // Don't show if debug is disabled
  if (!config.enabled) {
    return null;
  }

  // Don't show in production unless explicitly enabled
  if (process.env.NODE_ENV === 'production' && !config.enabled) {
    return null;
  }

  const positionClasses = {
    'bottom-right': 'bottom-4 right-4',
    'bottom-left': 'bottom-4 left-4',
    'top-right': 'top-4 right-4',
    'top-left': 'top-4 left-4'
  };

  const hasErrors = stats.entriesByLevel.error > 0;
  const hasWarnings = stats.entriesByLevel.warn > 0;
  
  return (
    <>
      {/* Floating Toggle Button */}
      <div 
        className={`fixed ${positionClasses[position]} z-50`}
        style={{
          position: 'fixed',
          bottom: '16px',
          right: '16px',
          zIndex: 9999
        }}
      >
        {/* Main Debug Button */}
        <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsPanelOpen(true);
            }}
            className={`relative p-3 rounded-full shadow-lg transition-all ${
              hasErrors 
                ? 'bg-red-500 text-white animate-pulse' 
                : hasWarnings
                ? 'bg-yellow-500 text-white'
                : config.enabled
                ? 'bg-green-500 text-white'
                : 'bg-gray-500 text-white'
            }`}
            title="Open Debug Panel"
            style={{
              backgroundColor: hasErrors ? '#ef4444' : hasWarnings ? '#f59e0b' : '#10b981',
              color: 'white',
              border: 'none',
              borderRadius: '50%',
              padding: '12px',
              cursor: 'pointer',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '48px',
              height: '48px',
              position: 'relative'
            }}
          >
            <Bug className="w-5 h-5" />
            
            {/* Entry Count Badge */}
            {stats.totalEntries > 0 && (
              <span 
                className="absolute -top-2 -right-2 bg-blue-600 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center"
                style={{
                  position: 'absolute',
                  top: '-8px',
                  right: '-8px',
                  backgroundColor: '#2563eb',
                  color: 'white',
                  fontSize: '12px',
                  borderRadius: '50%',
                  width: '24px',
                  height: '24px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {stats.totalEntries > 99 ? '99+' : stats.totalEntries}
              </span>
            )}
            
            {/* Error/Warning Indicators */}
            {hasErrors && (
              <span 
                className="absolute -top-1 -left-1 w-3 h-3 bg-red-600 rounded-full border-2 border-white"
                style={{
                  position: 'absolute',
                  top: '-4px',
                  left: '-4px',
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#dc2626',
                  borderRadius: '50%',
                  border: '2px solid white'
                }}
              />
            )}
            {hasWarnings && !hasErrors && (
              <span 
                className="absolute -top-1 -left-1 w-3 h-3 bg-yellow-500 rounded-full border-2 border-white"
                style={{
                  position: 'absolute',
                  top: '-4px',
                  left: '-4px',
                  width: '12px',
                  height: '12px',
                  backgroundColor: '#eab308',
                  borderRadius: '50%',
                  border: '2px solid white'
                }}
              />
            )}
          </button>
      </div>

      {/* Debug Panel */}
      <DebugPanel
        isOpen={isPanelOpen}
        onClose={() => setIsPanelOpen(false)}
      />
    </>
  );
};
